/**
 * Notification sound utility
 * Plays a notification sound when enabled
 */

const NOTIFICATION_SOUND_ENABLED_KEY = 'notification-sound-enabled';

/**
 * Check if notification sound is enabled
 */
export const isNotificationSoundEnabled = (): boolean => {
  const stored = localStorage.getItem(NOTIFICATION_SOUND_ENABLED_KEY);
  // Default to true (enabled) if not set
  return stored === null ? true : stored === 'true';
};

/**
 * Set notification sound enabled/disabled
 */
export const setNotificationSoundEnabled = (enabled: boolean): void => {
  localStorage.setItem(NOTIFICATION_SOUND_ENABLED_KEY, enabled.toString());
};

/**
 * Play notification sound using Web Audio API
 * Uses a simple beep sound
 */
export const playNotificationSound = (): void => {
  if (!isNotificationSoundEnabled()) {
    return;
  }

  try {
    // Create audio context
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Create oscillator (tone generator)
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configure sound
    oscillator.type = 'sine'; // Sine wave for smooth tone
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800 Hz

    // Configure volume envelope (fade in/out)
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01); // Fade in
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2); // Fade out

    // Play sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2); // 200ms duration

    // Clean up
    oscillator.onended = () => {
      audioContext.close();
    };
  } catch (error) {
    console.error('Failed to play notification sound:', error);
  }
};
